import * as React from 'react';
import { useRef, useReducer, useMemo, useCallback } from 'react';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Material UI Packages. */
import { Box, Chip, Slide, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';

/** Material UI Icons. */
import CheckIcon from '@mui/icons-material/Check';

/** Internal Components. */
import CustomSnackbar from 'shared/ui/CustomSnackbar';
import {
    tasksByshipmentType,
    progressByShipmentType,
    progressOption,
    settingOption,
} from 'shared/config/constantsData';

import { initApplySetting } from 'data/tradePartnerConfig';
import ApplySettingToggleHandler from './Handler/ApplySettingToggleHandler';
import ApplySettingControlHandler from './Handler/ApplySettingControlHandler';
import ApplySettingNoteHandler from './Handler/ApplySettingNoteHandler';
import ApplySettingHeader from './ApplySettingHeader';
import ApplySettingController from './ApplySettingController';
import PresetNote from './PresetNote';

const SHIPMENT_CATEGORIES = [
    { label: 'Ocean Import', index: 0, renderKey: 'il_preset_ocean_import' },
    { label: 'Ocean Export', index: 1, renderKey: 'il_preset_ocean_export' },
    { label: 'Air Import', index: 2, renderKey: 'il_preset_air_import' },
    { label: 'Air Export', index: 3, renderKey: 'il_preset_air_export' },
    { label: 'FTL / LTL', index: 4, renderKey: 'il_preset_ftl_ltl' },
];

/** Reducer definition. */
function reducer(state, action) {
    switch (action.type) {
        case 'SET_MULTIPLE':
            return {
                ...state,
                ...action.payload,
            };
        default:
            return state;
    }
}
/** Apply setting chip component. */
const SettingChip = ({ option, value, onClick, sx }) => (
    <Chip
        label={option.label}
        variant="outlined"
        onClick={onClick}
        sx={{
            mr: '3.5px',
            color: value ? '#ffffff' : '#c1c1c1',
            backgroundColor: value ? '#00647e' : '#ffffff',
            '&.MuiChip-outlined:hover': {
                backgroundColor: value ? '#2488a2' : '#ffffff',
                color: value ? '#ffffff' : '#000000',
                boxShadow: 'none',
            },
            ...sx,
        }}
    />
);

/** Preset note chip component based on the progress option. */
const ProgressChip = ({ option, value, hasNote, onClick }) => (
    <Tooltip title={option.label}>
        <Chip
            label={
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                    {hasNote && <CheckIcon sx={{ fontSize: 10 }} />}
                    <Typography sx={{ fontSize: 13 }}>{option.shortLabel}</Typography>
                </Box>
            }
            variant="outlined"
            onClick={onClick}
            sx={{
                width: 35,
                height: 35,
                mr: '3.5px',
                borderRadius: 10,
                color: value ? '#ffffff' : '#c1c1c1',
                backgroundColor: value ? '#00647e' : '#ffffff',
                '&.MuiChip-outlined:hover': {
                    backgroundColor: value ? '#2488a2' : '#ffffff',
                    color: value ? '#ffffff' : '#000000',
                    boxShadow: 'none',
                },
            }}
        />
    </Tooltip>
);

/** Category section component. */
const CategorySection = ({
    category,
    renderValue,
    mode,
    onChipClick,
    onSelectAll,
    selectedNote,
    presetNoteForm,
    onNoteChip,
}) => {
    const isEditable = mode === 'create' || mode === 'edit';
    const isNoteMode = mode === 'note';

    return (
        <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography
                    onClick={isEditable ? () => onSelectAll(category.index, category.renderKey) : undefined}
                    sx={{
                        fontSize: 11,
                        mb: 0.5,
                        borderRadius: 2,
                        cursor: isEditable ? 'pointer' : 'auto',
                        backgroundColor: isEditable ? '#80b2bf' : 'auto',
                        color: isEditable ? 'white' : 'black',
                        px: isEditable ? 1 : 0,
                    }}
                >
                    {category.label}
                </Typography>
            </Box>

            {!isNoteMode
                ? // Setting chips
                  tasksByshipmentType[category.index].map((task) => {
                      const option = settingOption.find((opt) => opt.value === task);
                      const value = renderValue[category.renderKey]?.[option.value] || false;

                      return (
                          <SettingChip
                              key={option.value}
                              option={option}
                              value={value}
                              onClick={() => onChipClick(option)}
                          />
                      );
                  })
                : // Preset note chips
                  progressByShipmentType[category.index].map((task, key) => {
                      const option = progressOption.find((opt) => opt.value === task);
                      const value =
                          selectedNote.index === category.index && selectedNote.progress.value === option.value;

                      const hasNote = presetNoteForm.find(
                          (noteForm) =>
                              noteForm.il_preset_id === renderValue.id &&
                              noteForm.shipment_type === category.index &&
                              noteForm.attribute === task
                      );

                      return (
                          <ProgressChip
                              key={key}
                              option={option}
                              value={value}
                              hasNote={hasNote}
                              onClick={() => onNoteChip(category.index, option)}
                          />
                      );
                  })}
        </Box>
    );
};

export default function ApplySetting(props) {
    /** Get props from the state defined in TradePartner.js.  */
    const { selectedRow } = props;
    /** Get props from the state defined in EditTradePartnerDialog.js.  */
    const { applySettingData, setApplySettingData, dirtyFlags, setDirtyFlags } = props;

    /** Dispatch a function to update an edited trade partner. */
    const reduxDispatch = useDispatch();
    /** Redux state for managing all preset notes of the currently selected trade partner. */
    const presetNoteForm = useSelector((state) => state.presetNoteForm);

    /** Reducer state that includes renderValue, presetName, selectedNote, mode, and initEditValue. */
    const [presetUIState, dispatch] = useReducer(reducer, initApplySetting);
    /** Destructure state. */
    const { renderValue, selectedNote, mode } = presetUIState;

    /** Reference to the preset name input field for managing focus. */
    const presetNameInputRef = useRef(null);

    /** Load toggle button group handler. */
    const { handleModeChange, handleNoteEditActive } = ApplySettingToggleHandler({
        presetUIState,
        dispatch,
        selectedRow,
        presetNameInputRef,
        applySettingData,
        setDirtyFlags,
    });

    /** Load preset controller handlers. */
    const { handleAdd, handleDelete, handlePresetList, handleUpdate } = ApplySettingControlHandler({
        presetUIState,
        dispatch,
        applySettingData,
        setApplySettingData,
        setDirtyFlags,
        handleNoteEditActive,
    });

    /** Load note handlers. */
    const { handleNoteChip, handleNoteContent } = ApplySettingNoteHandler({
        presetUIState,
        dispatch,
        presetNoteForm,
        dirtyFlags,
        setDirtyFlags,
        reduxDispatch,
    });

    /** Handler for toggling the selection of preset options, and it only works in edit mode. */
    const handleChipClick = useCallback(
        (option, key) => {
            if (mode === 'view') return;

            dispatch({
                type: 'SET_MULTIPLE',
                payload: {
                    renderValue: {
                        ...renderValue,
                        [key]: {
                            ...renderValue[key],
                            [option.value]: !renderValue[key]?.[option.value],
                        },
                    },
                },
            });
        },
        [mode, renderValue]
    );

    /** Function to toggle the select all button for a single category's settings. */
    const handleSelectAll = useCallback(
        (index, renderKey) => {
            const updatedRenderValue = {
                ...renderValue,
                [renderKey]: Object.keys(renderValue[renderKey]).reduce((updatedObject, key) => {
                    if (key === 'id' || key === 'il_preset_id') {
                        updatedObject[key] = renderValue[renderKey][key];
                    } else {
                        updatedObject[key] = !Object.keys(renderValue[renderKey]).every(
                            (k) => k === 'id' || k === 'il_preset_id' || renderValue[renderKey][k] === true
                        );
                    }
                    return updatedObject;
                }, {}),
            };

            dispatch({
                type: 'SET_MULTIPLE',
                payload: { renderValue: updatedRenderValue },
            });
        },
        [renderValue]
    );

    /** Define individual chip click handlers for each shipment category. */
    const categoriesWithHandlers = useMemo(
        () =>
            SHIPMENT_CATEGORIES.map((category) => ({
                ...category,
                handler: (option) => handleChipClick(option, category.renderKey),
            })),
        [handleChipClick]
    );

    return (
        <Box sx={{ p: 2.5, pt: 1.8, pl: 0 }}>
            {/* Header and Controller */}
            <Box sx={{ mb: 1 }}>
                <ApplySettingHeader
                    mode={mode}
                    applySettingData={applySettingData}
                    handleModeChange={handleModeChange}
                />
                <ApplySettingController
                    presetUIState={presetUIState}
                    dispatch={dispatch}
                    applySettingData={applySettingData}
                    presetNameInputRef={presetNameInputRef}
                    handlePresetList={handlePresetList}
                    handleAdd={handleAdd}
                    handleDelete={handleDelete}
                    handleUpdate={handleUpdate}
                />
            </Box>

            {/* Main Content */}
            <Grid container spacing={2}>
                <Grid size={mode === 'note' ? 8 : 12}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: '10px',
                            boxShadow: '0px 1px 2px 1px rgba(0, 0, 0, 0.1)',
                            p: 1.5,
                            gap: 1,
                            bgcolor: 'white',
                        }}
                    >
                        {categoriesWithHandlers.map((category) => (
                            <CategorySection
                                key={category.index}
                                category={category}
                                renderValue={renderValue}
                                mode={mode}
                                onChipClick={category.handler}
                                onSelectAll={handleSelectAll}
                                selectedNote={selectedNote}
                                presetNoteForm={presetNoteForm}
                                onNoteChip={handleNoteChip}
                            />
                        ))}
                    </Box>
                </Grid>

                {/* Note Panel */}
                {mode === 'note' && (
                    <Slide direction="left" in={mode === 'note'} mountOnEnter unmountOnExit>
                        <Grid size={4}>
                            <PresetNote selectedNote={selectedNote} handleNoteContent={handleNoteContent} />
                        </Grid>
                    </Slide>
                )}
            </Grid>
            <CustomSnackbar />
        </Box>
    );
}
